import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';
import Header from './components/Header';
import FdHeader from './components/FdHeader';
import FdFooter from './components/FdFooter';
import FdTerms from './components/FdTerms';
import FdPrivacy from './components/FdPrivacy';
import Footer from './components/Footer';
import Landing from './components/Landing';
import Pricing from './components/Pricing';
import ObituaryForm from './components/ObituaryForm';
import ObituaryDisplay from './components/ObituaryDisplay';
import Redeem from './components/Redeem';
import Help from './components/Help';
import Terms from './components/Terms';
import Privacy from './components/Privacy';
import About from './components/About';
import PaymentSuccess from './components/PaymentSuccess';
import ExitIntentPopup from './components/ExitIntentPopup';
import RainBackground from './components/RainBackground';
import Blog from './components/Blog';
import BlogPost from './components/BlogPost';
import LeadMagnet from './components/LeadMagnet';
import SeoPage, { SEO_PATHS } from './components/SeoPage';
import Admin from './components/Admin';
import FuneralDirectorsLanding from './components/FuneralDirectorsLanding';
import FuneralDirectorDashboard from './components/FuneralDirectorDashboard';

function App() {
  const [currentStep, setCurrentStep] = useState('landing'); // 'landing', 'pricing', 'form', 'display', 'redeem', 'help', 'terms', 'privacy', 'about'
  const [obituaryData, setObituaryData] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);

  // Handle routing based on URL path
  useEffect(() => {
    const path = window.location.pathname;
    const urlParams = new URLSearchParams(window.location.search);
    const shouldShowCheckout = urlParams.get('checkout') === 'true';
    const paymentSuccess = urlParams.get('payment') === 'success';
    const paymentCancelled = urlParams.get('payment') === 'cancelled';
    
    if (paymentSuccess) {
      setCurrentStep('payment-success');
      // Clean up URL
      window.history.replaceState({ step: 'payment-success' }, '', '/');
    } else if (paymentCancelled) {
      setCurrentStep('pricing');
      // Clean up URL
      window.history.replaceState({ step: 'pricing' }, '', '/');
    } else if (path === '/redeem') {
      setCurrentStep('redeem');
      window.history.replaceState({ step: 'redeem' }, '', '/redeem');
    } else if (path === '/help') {
      setCurrentStep('help');
      window.history.replaceState({ step: 'help' }, '', '/help');
    } else if (path === '/terms') {
      setCurrentStep('terms');
      window.history.replaceState({ step: 'terms' }, '', '/terms');
    } else if (path === '/privacy') {
      setCurrentStep('privacy');
      window.history.replaceState({ step: 'privacy' }, '', '/privacy');
    } else if (path === '/about') {
      setCurrentStep('about');
      window.history.replaceState({ step: 'about' }, '', '/about');
    } else if (path.startsWith('/blog/')) {
      setCurrentStep('blog-post');
      // Don't modify URL for blog posts
    } else if (path === '/blog') {
      setCurrentStep('blog');
      window.history.replaceState({ step: 'blog' }, '', '/blog');
    } else if (path === '/free-obituary-checklist') {
      setCurrentStep('lead-magnet');
      window.history.replaceState({ step: 'lead-magnet' }, '', '/free-obituary-checklist');
    } else if (path === '/admin') {
      setCurrentStep('admin');
      window.history.replaceState({ step: 'admin' }, '', '/admin');
    } else if (path === '/funeral-directors' || path === '/funeral-directors/') {
      setCurrentStep('fd-landing');
      window.history.replaceState({ step: 'fd-landing' }, '', '/funeral-directors');
    } else if (path === '/funeral-directors/terms') {
      setCurrentStep('fd-terms');
      window.history.replaceState({ step: 'fd-terms' }, '', '/funeral-directors/terms');
    } else if (path === '/funeral-directors/privacy') {
      setCurrentStep('fd-privacy');
      window.history.replaceState({ step: 'fd-privacy' }, '', '/funeral-directors/privacy');
    } else if (path.startsWith('/funeral-directors/dashboard')) {
      setCurrentStep('fd-dashboard');
      // Preserve ?tab=... and ?payment=... so the dashboard can honor them
      // (e.g. browser back from Stripe → ?tab=credits).
      window.history.replaceState({ step: 'fd-dashboard' }, '', path + window.location.search);
    } else if (SEO_PATHS.includes(path)) {
      setCurrentStep('seo-page');
      window.history.replaceState({ step: 'seo-page' }, '', path);
    } else if (shouldShowCheckout) {
      setCurrentStep('pricing');
      window.history.replaceState({ step: 'pricing' }, '', '/');
    } else {
      // Default to landing page
      setCurrentStep('landing');
      window.history.replaceState({ step: 'landing' }, '', '/');
    }
    
    // Restore scroll position after page reload (from resize)
    const savedScrollPosition = sessionStorage.getItem('scrollPosition');
    if (savedScrollPosition) {
      setTimeout(() => {
        window.scrollTo(0, parseInt(savedScrollPosition, 10));
        sessionStorage.removeItem('scrollPosition');
      }, 100);
    }
  }, []);

  // First-party page-view tracking. Sends one ping on route change (including
  // initial load). The session id is a per-tab random token kept in
  // sessionStorage so the Traffic admin can count unique sessions without
  // using cookies or third-party analytics.
  useEffect(() => {
    try {
      let sid = sessionStorage.getItem('pv_sid');
      if (!sid) {
        sid = (Date.now().toString(36) + Math.random().toString(36).slice(2, 10));
        sessionStorage.setItem('pv_sid', sid);
      }
      const isFd = window.location.pathname.startsWith('/funeral-directors');
      fetch('/api/track-view', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          path: window.location.pathname || '/',
          referrer: document.referrer || null,
          sessionId: sid,
          isFd,
        }),
        keepalive: true,
      }).catch(() => {});
    } catch (_) { /* analytics failures must never break the app */ }
  }, [currentStep]);

  // Handle browser back/forward buttons
  useEffect(() => {
    // Ensure landing page is always in history as a fallback
    if (!window.history.state || !window.history.state.step) {
      window.history.replaceState({ step: 'landing' }, '', '/');
    }

    const handlePopState = (event) => {
      // If there's state, use it
      if (event.state && event.state.step) {
        setCurrentStep(event.state.step);
      } else {
        // If no state (trying to leave site), prevent it and go to landing
        setCurrentStep('landing');
        window.history.pushState({ step: 'landing' }, '', '/');
      }
    };

    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  const handleGetStarted = () => {
    setCurrentStep('pricing');
    window.history.pushState({ step: 'pricing' }, '', '/');
    // Smooth scroll to pricing
    setTimeout(() => {
      const element = document.getElementById('pricing');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  const handlePaymentSuccess = (plan) => {
    setSelectedPlan(plan);
    setCurrentStep('form');
  };

  const handleGenerateObituary = (data) => {
    setObituaryData(data);
    setCurrentStep('display');
  };

  const handleNewObituary = () => {
    setObituaryData(null);
    setCurrentStep('landing');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // The Funeral Professional portal has its own visual system (light,
  // glassmorphic, pastel) that clashes with the consumer rain + corner
  // scrollwork. Hide those chrome pieces on FD pages so the portal reads
  // as a distinct product.
  // The Funeral Professional portal (both the marketing landing AND the
  // signed-in dashboard) uses its own visual system. Everything below —
  // backdrop, header, footer, and the scoped `.fd-portal-active` styles —
  // is keyed off this flag.
  const isFdPortal =
    currentStep === 'fd-dashboard' ||
    currentStep === 'fd-landing' ||
    currentStep === 'fd-terms' ||
    currentStep === 'fd-privacy';

  return (
    <div className={`App ${isFdPortal ? 'fd-portal-active' : ''}`}>
      {!isFdPortal && (
        <>
          <RainBackground />
          <div className="page-decoration decoration-top-left"></div>
          <div className="page-decoration decoration-top-right"></div>
          <div className="page-decoration decoration-bottom-left"></div>
          <div className="page-decoration decoration-bottom-right"></div>
        </>
      )}

      {isFdPortal && (
        // Portal-only ambient backdrop: soft pastel orbs + faint quill /
        // peace-lily watermarks. Fixed position + aria-hidden so it never
        // gets in the way of screen readers or click targets.
        <div className="fd-portal-backdrop" aria-hidden="true">
          <div className="fd-orb fd-orb-violet" />
          <div className="fd-orb fd-orb-mint" />
          <div className="fd-orb fd-orb-blush" />
          <div className="fd-watermark fd-watermark-quill" />
          <div className="fd-watermark fd-watermark-lily" />
        </div>
      )}

      {isFdPortal ? (
        <FdHeader />
      ) : (
        <Header onGetStarted={handleGetStarted} />
      )}
      
      {currentStep === 'landing' && (
        <Landing onGetStarted={handleGetStarted} />
      )}

      {currentStep === 'pricing' && (
        <Pricing 
          onPaymentSuccess={handlePaymentSuccess}
          autoShowPayment={new URLSearchParams(window.location.search).get('checkout') === 'true'}
        />
      )}

      {currentStep === 'form' && (
        <div className="container py-5">
          <div className="row justify-content-center">
            <div className="col-lg-10 col-xl-8">
              <div className="glass-card mb-4 text-center">
                <h1 className="commercial-heading mb-3">Create Your Obituary</h1>
                <p className="commercial-subheading mb-0">
                  Fill in the details to generate a beautiful, professional obituary
                </p>
                {selectedPlan && (
                  <div className="mt-3">
                    <span className="badge bg-success">Package: {selectedPlan.name}</span>
                  </div>
                )}
              </div>
              <ObituaryForm onGenerate={handleGenerateObituary} />
            </div>
          </div>
        </div>
      )}

      {currentStep === 'display' && (
        <div className="container py-5">
          <div className="row justify-content-center">
            <div className="col-lg-10 col-xl-8">
              <ObituaryDisplay 
                data={obituaryData} 
                onNewObituary={handleNewObituary}
              />
            </div>
          </div>
        </div>
      )}

      {currentStep === 'redeem' && (
        <Redeem />
      )}

      {currentStep === 'help' && (
        <Help />
      )}

      {currentStep === 'terms' && (
        <Terms />
      )}

      {currentStep === 'privacy' && (
        <Privacy />
      )}

      {currentStep === 'about' && (
        <About />
      )}

      {currentStep === 'payment-success' && (
        <PaymentSuccess />
      )}

      {currentStep === 'blog' && (
        <Blog onNavigateToPost={(slug) => {
          window.history.pushState({ step: 'blog-post' }, '', `/blog/${slug}`);
          setCurrentStep('blog-post');
        }} />
      )}

      {currentStep === 'blog-post' && (
        <BlogPost slug={window.blogPostSlug} />
      )}

      {currentStep === 'lead-magnet' && (
        <LeadMagnet />
      )}

      {currentStep === 'seo-page' && (
        <SeoPage onGetStarted={handleGetStarted} />
      )}

      {currentStep === 'admin' && (
        <Admin />
      )}

      {currentStep === 'fd-landing' && (
        <FuneralDirectorsLanding />
      )}

      {currentStep === 'fd-dashboard' && (
        <FuneralDirectorDashboard />
      )}

      {currentStep === 'fd-terms' && (
        <FdTerms />
      )}

      {currentStep === 'fd-privacy' && (
        <FdPrivacy />
      )}

      {/* Exit Intent Popup - Only show on landing page */}
      {currentStep === 'landing' && (
        <ExitIntentPopup onGetStarted={handleGetStarted} />
      )}

      {isFdPortal ? (
        <FdFooter />
      ) : (
        <Footer onGetStarted={handleGetStarted} />
      )}
    </div>
  );
}

export default App;
